import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Account, AccountResource, Invitation, InvitationResource, Organization, OrganizationResource, OrgKit } from '@apaq/orgkit-js';
import { Passcode, User } from '@apaq/passport-control-js';
import { isPast } from 'date-fns';
import jwt_decode from "jwt-decode";
import log from 'loglevel';
import { AuthenticationService, ContextService } from 'ngx-orgkit';
import { errorHandler } from 'src/app/app.module';
import { LinkService } from 'src/app/services/link.service';
import { environment } from 'src/environments/environment';

declare var window: Window & {
  __passport_control__: {
    host: string
  },
  __orgkit__: {
    host: string
  }
};

@Component({
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent implements AfterViewInit {

  @ViewChild('spinnerAnimation') spinnerAnimation?: ElementRef;
  @ViewChild('contentAnimation') contentAnimation?: ElementRef;
  view: 'enter_email' | 'enter_otp' | 'accept_terms' | 'accept-invitation' = 'enter_email';
  loading = false;
  email: string = '';
  passcode?: Passcode;
  user?: User;
  validationError?: string;
  authenticating = true;
  initialized = false;

  hasInvitation = false;
  invitation?: Invitation;
  invitingOrganization?: Organization;

  constructor(
    private authenticationService: AuthenticationService,
    private context: ContextService,
    private linkService: LinkService,
    private accountResource: AccountResource,
    private invitationResource: InvitationResource) {

      authenticationService.beforeAuthentication.subscribe(() => {
        Stallion.toast($localize`Logging in...`, 'info', undefined, 1000);
      })

    log.debug('Current url: ', location.href);
    const params = new URLSearchParams(window.location.search);
    if (params.get('invitation') != null) {
      const value = params.get('invitation') ?? undefined;
      if (value) {
        this.hasInvitation = true;

        // Remove the invitation from history
        const url = new URL(location.href);
        url.searchParams.delete('invitation');
        log.debug('Current url 2: ', location.href);
    
        setTimeout(() => {
          history.replaceState(null, '', url);
          log.debug('Current url 3: ', location.href);
    
        }, 1000);


        this.resolveInvitation(value);
      }
    }
  }

  get passportApiUrl() {
    return window.__passport_control__?.host ?? 'http://localhost:9000';
  }

  get termsUrl() {
    return environment.externalUrls.terms;
  }

  async ngAfterViewInit() {
    this.context.contextChange.subscribe(async _ => {
      if (!this.hasInvitation && this.context.account) {
        if (!this.hasInvitation) {
          this.redirectAfterLogin();
        }
      }
    });

    if (!this.hasInvitation && this.context.account) {
      if (!this.hasInvitation) {
        this.redirectAfterLogin();
      }
    }

    try {
      await this.authenticationService.isAuthenticated()
    } catch(error) {
      log.error(error);
    }
    this.initialized = true;
    

  }

  get signInDescription() {
    if (this.invitation == null || this.invitingOrganization == null) {
      return undefined;
    }

    return $localize`You have been invited to be part ${this.invitingOrganization.name}`;
  }

  public async onAuthenticate(details: { token: string, email: string, userId: string }) {
    console.log('User authenticated. ');

    const tokenDec = jwt_decode(details.token) as any;

    // If user do not exist, create a user.
    const userExists = !await this.accountResource.isUsernameAvailable(tokenDec.sub);
    if (!userExists) {

      try {
        const account: Partial<Account> = {
          email: details.email,
          name: details.email,
          login: tokenDec.sub,
          languageCode: 'da'
        };
        await this.accountResource.save(account as Account, details.token);
      } catch (error) {
      }
    }
    const tokenDetails = await this.accountResource.authenticate(details.token);
    this.authenticationService.refreshToken = details.token;
    this.authenticationService.token = tokenDetails.token;

    if (this.invitation) {
      await this.invitationResource.accept(this.invitation.id);
      this.context.organizationId = this.invitingOrganization?.id;
      this.context.reloadAccount();
    }
    this.redirectAfterLogin();
  }
  /*
    private displayError(error: any) {
      let message = error + '';
      if (error instanceof HttpErrorResponse) {
        message = error.message;
      }
      Stallion.alert(message);
    }
  */
  private async resolveInvitation(jwt: string) {
    try {
      const jwtDec = jwt_decode(jwt) as any;
      const id = jwtDec.sub;

      // We create a new orgkit client for this so we do not disturb anything in authenticateion service.
      const orgkit = new OrgKit({
        getOrganizationId: () => Promise.resolve(null!),
        getToken: () => Promise.resolve(jwt),
        host: window.__orgkit__?.host,
        errorHandler: errorHandler
      });

      const invitation = await orgkit.invitations.findById(id);
      if (invitation.accepted || isPast(invitation.expireDate)) {
        this.hasInvitation = false;
        return;
      }

      this.invitation = invitation;
      this.invitingOrganization = await orgkit.organizations.findById('current');
      this.email = this.invitation.email;
    } catch(error) {
      Stallion.toast(error as string, 'info');
      this.hasInvitation = false;
    }
  }

  private redirectAfterLogin() {
    console.log('Redirecting after login. ');
    if (this.context.organizationId) {
      this.linkService.openPlan();
    } else {
      this.linkService.openOrganizations();
    }
  }
}
